import { useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { enUS, urPK } from "@mui/material/locale";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTheme } from "../contexts/ThemeContext";
import { getFontStackForLanguage } from "../utils/appFontStack";
import { resolveBaseLanguage } from "../i18n";

const urduTypographyOverrides = {
  body1: { lineHeight: 1.65 },
  body2: { lineHeight: 1.65 },
  subtitle1: { lineHeight: 1.55 },
  subtitle2: { lineHeight: 1.55 },
  h1: { lineHeight: 1.35 },
  h2: { lineHeight: 1.35 },
  h3: { lineHeight: 1.4 },
  h4: { lineHeight: 1.4 },
  h5: { lineHeight: 1.45 },
  h6: { lineHeight: 1.45 },
  button: { lineHeight: 1.5 },
};

/** MUI palette prefers opaque colors; strip #RRGGBBAA alpha when present. */
function solidColor(value, fallback) {
  if (!value || typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (/^#[0-9a-fA-F]{8}$/.test(trimmed)) return trimmed.slice(0, 7);
  return trimmed;
}

export default function ThemeConfig({ children }) {
  const { theme: appTheme, customColors } = useTheme();
  const { i18n } = useTranslation();
  const language = resolveBaseLanguage(i18n.resolvedLanguage || i18n.language || "en");
  const isUrdu = language === "ur";
  /** Keep the app shell (sidebar, main drawer anchors) in LTR; Urdu form fields use `FormDirectionScope`. */
  const direction = "ltr";
  const fontFamily = getFontStackForLanguage(language);
  const dayjsLocale = isUrdu ? "ur" : "en";
  const muiLocale = isUrdu ? urPK : enUS;
  const isDark = appTheme === "dark";

  const theme = useMemo(
    () =>
      createTheme(
        {
          direction,
          palette: {
            mode: isDark ? "dark" : "light",
            primary: {
              main: solidColor(customColors?.theme_primary, "#2a9988"),
            },
            secondary: {
              // Color scheme secondary is a dark surface; light mode keeps a light paper tone.
              main: isDark
                ? solidColor(customColors?.theme_secondary, "#0f2e36")
                : "#ffffff",
            },
            text: {
              primary: isDark
                ? solidColor(customColors?.text_primary, "#90a7ad")
                : "#2c3e50",
            },
            // Dark mode follows affiliate color_scheme; light keeps CSS .light defaults.
            background: {
              default: isDark
                ? solidColor(customColors?.background_primary, "rgba(9, 35, 41, 1)")
                : "#f5f7fa",
              paper: isDark
                ? solidColor(customColors?.theme_secondary, "#0f2e36")
                : "#ffffff",
            },
          },
          typography: {
            fontFamily,
            ...(isUrdu ? urduTypographyOverrides : {}),
          },
          components: {
            MuiButton: {
              styleOverrides: {
                startIcon: ({ theme: muiTheme }) => ({
                  marginLeft: 0,
                  marginRight: 0,
                  marginInlineStart: "-4px",
                  marginInlineEnd: muiTheme.spacing(isUrdu ? 1.5 : 1),
                }),
                endIcon: ({ theme: muiTheme }) => ({
                  marginLeft: 0,
                  marginRight: 0,
                  marginInlineStart: muiTheme.spacing(isUrdu ? 1.5 : 1),
                  marginInlineEnd: "-4px",
                }),
              },
            },
            MuiPaper: {
              styleOverrides: {
                root: {
                  boxShadow: "none",
                  backgroundImage: "none",
                },
              },
            },
            MuiCard: {
              styleOverrides: {
                root: {
                  boxShadow: "none",
                  backgroundImage: "none",
                },
              },
            },
          },
        },
        muiLocale,
      ),
    [appTheme, customColors, fontFamily, isDark, isUrdu, muiLocale],
  );

  return (
    <ThemeProvider theme={theme}>
      <div
        className="mui-direction-root"
        data-mui-direction={direction}
        data-lang={language}
        style={{ display: "contents" }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
          {children}
        </LocalizationProvider>
      </div>
    </ThemeProvider>
  );
}
